import type { GeoJsonGeometry, Pos } from '../lib/geo.ts'
import { EARTH_RADIUS_M, targetPoint } from './guide.ts'
import type { LookShape, Pt, Ring } from './lookGeometry.ts'

/** M25-P (docs/M25.md C and E, ADR-129/132): THE REAL OUTLINE OF AN AREA AS A PICTURE — pure, tested in greenOutline.test.ts.
 *  The polygon in force is projected around its centroid in metres (`targetPoint`: area-weighted, holes out; a flat projection, fine at the size of a
 *  green), turned by −bearing so that the play arrives at the bottom (6) and the back is at the top (12), holes kept, and fitted to the box (48 × 48 by
 *  default) with its aspect. The stripes are laid on it by `lookGeometry.drawLook` and clipped to it by the SVG (a clipPath, never a computed
 *  intersection). The bearing is the line of play INTO the area (part L, `data/playLine.ts`), degrees clockwise from true north; it turns the drawing
 *  and nothing else (rule 5). No bearing, no outline: the caller draws the generic shape and says the line of play is unknown (rule 6). */

const M_PER_DEG = (EARTH_RADIUS_M * Math.PI) / 180
const rad = (d: number) => (d * Math.PI) / 180

export interface OutlineFit extends LookShape {
  /** any other position on the same drawing (an approach, a tee: where an arrow would start) */
  toBox(p: Pos): Pt
  /** box units per metre */
  readonly scale: number
}

const isPos = (p: unknown): p is Pos => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])
/** a ring as given, without its closing point, if it has three positions at least */
function readRing(r: unknown): Pos[] | null {
  if (!Array.isArray(r) || !r.every(isPos)) return null
  const pts = r as Pos[]
  const open = pts.length > 1 && pts[0]![0] === pts[pts.length - 1]![0] && pts[0]![1] === pts[pts.length - 1]![1] ? pts.slice(0, -1) : pts
  return open.length >= 3 ? open : null
}

/** The outline fitted to the box, turned to its line of play; null for a shape it cannot read (not a polygon, no ring of three points, no size) or a
 *  bearing that is not a number. `pad` keeps the stroke inside the box; `bottom` leaves room under the outline (the arrow of the play arriving). */
export function greenOutline(geom: GeoJsonGeometry | null | undefined, bearingDeg: number, opts: { box?: number; pad?: number; bottom?: number } = {}): OutlineFit | null {
  const box = opts.box ?? 48, pad = opts.pad ?? 3, bottom = opts.bottom ?? 0
  if (!geom || !Number.isFinite(bearingDeg) || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) return null
  const polys = (geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates) as unknown[]
  const outersPos: Pos[][] = [], holesPos: Pos[][] = []
  for (const poly of polys) {
    if (!Array.isArray(poly)) continue
    const rings = poly.map(readRing)
    if (!rings[0]) continue
    outersPos.push(rings[0])
    for (const h of rings.slice(1)) if (h) holesPos.push(h)
  }
  if (!outersPos.length) return null
  const origin = targetPoint(geom)
  if (!origin || !Number.isFinite(origin[0]) || !Number.isFinite(origin[1])) return null
  const kx = M_PER_DEG * Math.cos(rad(origin[1])), cb = Math.cos(rad(bearingDeg)), sb = Math.sin(rad(bearingDeg))
  // metres east (e) and north (n) of the centroid, turned so that the bearing points up: u to the right of the play, v along it; SVG y is −v
  const local = ([lon, lat]: Pos): Pt => {
    const e = (lon - origin[0]) * kx, n = (lat - origin[1]) * M_PER_DEG
    return { x: e * cb - n * sb, y: -(e * sb + n * cb) }
  }
  const outersL = outersPos.map((r) => r.map(local)), holesL = holesPos.map((r) => r.map(local))
  const all = outersL.flat()
  const minX = Math.min(...all.map((p) => p.x)), maxX = Math.max(...all.map((p) => p.x)), minY = Math.min(...all.map((p) => p.y)), maxY = Math.max(...all.map((p) => p.y))
  const w = maxX - minX, h = maxY - minY, availW = box - 2 * pad, availH = box - 2 * pad - bottom
  if (!(w > 1e-9 && h > 1e-9) || availW <= 0 || availH <= 0) return null
  const scale = Math.min(availW / w, availH / h)
  const ox = pad + (availW - w * scale) / 2, oy = pad + (availH - h * scale) / 2
  const fit = (p: Pt): Pt => ({ x: ox + (p.x - minX) * scale, y: oy + (p.y - minY) * scale })
  const outers: Ring[] = outersL.map((r) => r.map(fit)), holes: Ring[] = holesL.map((r) => r.map(fit))
  return { outers, holes, c: fit({ x: 0, y: 0 }), box, scale, toBox: (p: Pos) => fit(local(p)) }
}
